import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Container } from '../../../ui'
import { Button, Flex, Box, Copy, Icon, DotsLoader } from '@elparking/components'

import { FormattedMessage, injectIntl } from 'react-intl'
import messages from './messages'
import { LocalForm } from 'commons/js/components/form'
import DescriptionField from '../../../ui/DescriptionField'
import FilesField from '../../../ui/FilesField'
import ReasonField from '../../../ui/ReasonField'

import EmailField from 'commons/js/ui/EmailField'
import PhoneField, { messages as phoneMessages} from 'commons/js/ui/PhoneField'
import FirstNameField from 'commons/js/ui/FirstNameField'
import LastNameField from 'commons/js/ui/LastNameField'
import AddressField from 'commons/js/ui/AddressField'
import PostalCodeField from 'commons/js/ui/PostalCodeField'
import ProvinceField from '../../../ui/ProvinceField'
import TownField from 'commons/js/ui/TownField'
import LoaderButton from 'commons/js/ui/LoaderButton'
import useApi from 'commons/js/hooks/useApi'
import {
    required,
    length,
    number,
} from 'commons/js/util/validators'

const MAX_MBYTES_FILESIZE = 20

const Form = ({ intl, fine, onSubmit, onBack, getProvinces }) => {
    const [province, setProvince] = useState(null)

    const {
        response: { result: provinces },
        loading: loadingProvinces,
        sendRequest: requestProvinces,
    } = useApi(getProvinces)

    useEffect(() => {
        requestProvinces()
    }, [requestProvinces])

    return (
        <Container key='form' width='600px'>
            <LocalForm
              as='form'
              encType='multipart/form-data'
              onSubmit={(form) => {
                  onSubmit({
                      ...form,
                       townId: form[`town_${form.provinceId}`],
                  })
              }}
              model='claim'>
                <Flex flexWrap='wrap' mb='2em'>
                    <Box w='50%' pr='0.2em'>
                        <Button.unstyled type='button' onClick={onBack} disabled={fine.isCancelled}>
                            <Copy fontColor='main' transform='uppercase'>
                                <Icon type='left' /> <FormattedMessage defaultMessage='Ver detalle' />
                            </Copy>
                        </Button.unstyled>
                    </Box>
                    <Box pl='0.2em' w='50%' textAlign='right'>
                        <Copy fontWeight='book' fontSize='xxxSmall' color='gray60' transform='uppercase' padding='0'>
                            <FormattedMessage defaultMessage='Número de boletín' />
                        </Copy>
                        <Copy fontSize='medium' fontWeight='book' padding='0'>
                            {fine.bulletinNumber}
                        </Copy>
                    </Box>
                    <Box w='100%'>
                        <Box background='lightGray20' p='1em' mt='2em'>
                            <Copy transform='uppercase' padding='0 0 0.5em 0' fontSize='small' fontColor='gray60'>
                                <FormattedMessage defaultMessage='Motivo de la reclamación' />
                            </Copy>
                            <Box w='100%' mt='1em'>
                                <ReasonField
                                  dataTest='claim-reason-field'
                                  width='100%'
                                  validateIcon
                                  label={intl.formatMessage(messages.reason)}
                                />
                            </Box>
                            <Box w='100%' mt='1em'>
                                <DescriptionField
                                  dataTest='claim-description-field'
                                  rows='4'
                                  width='100%'
                                  label={intl.formatMessage(messages.description)}
                                />
                            </Box>
                        </Box>
                        <Box background='lightGray20' p='1em' mt='1em'>
                            <Copy transform='uppercase' padding='0 0 0.5em 0' fontSize='small' fontColor='gray60'>
                                <FormattedMessage defaultMessage='Datos personales' />
                            </Copy>
                            <Flex flexWrap='wrap'>
                                <Box w={['100%', '100%', '50%']} mt='1em' pr={['0', '0', '0.5em']} >
                                    <FirstNameField
                                      dataTest='claim-firstName-field'
                                      width='100%'
                                      validateIcon
                                      label={intl.formatMessage(messages.firstName)}
                                      placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                                    />
                                </Box><Box pl={['0', '0', '0.5em']} mt='1em' w={['100%', '100%', '50%']}>
                                    <LastNameField
                                      dataTest='claim-lastName-field'
                                      width='100%'
                                      validateIcon
                                      required
                                      label={intl.formatMessage(messages.lastName)}
                                      placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                                    />
                                </Box>
                            </Flex>
                            <Flex flexWrap='wrap'>
                                <Box w={['100%', '100%', '50%']} mt='1em' pr={['0', '0', '0.5em']} >
                                    <EmailField
                                      dataTest='claim-email-field'
                                      width='100%'
                                      validateIcon
                                      label={intl.formatMessage(messages.email)}
                                      placeholder={intl.formatMessage(messages.emailPlaceholder)}
                                      leftIcon={<Icon type='email' color='gray' />}
                                    />
                                </Box><Box pl={['0', '0', '0.5em']} mt='1em' w={['100%', '100%', '50%']}>
                                    <PhoneField
                                      dataTest='claim-phone-field'
                                      width='100%'
                                      validators={{
                                        required: required(),
                                        length: length({ min: 9, max: 20 }),
                                        number: number(),
                                      }}
                                      errorMessages={{
                                        required: intl.formatMessage(phoneMessages.requiredError),
                                        phone: intl.formatMessage(phoneMessages.phoneError),
                                        length: intl.formatMessage(phoneMessages.lengthError),
                                        number: intl.formatMessage(phoneMessages.number),
                                      }}
                                      label={intl.formatMessage(messages.phone)}
                                      leftIcon={<Icon type='phone' color='gray' />}
                                    />
                                </Box>
                            </Flex>
                            <Copy transform='uppercase' padding='2em 0 0.5em 0' fontSize='small' fontColor='gray60'>
                                <FormattedMessage defaultMessage='Dirección postal' />
                            </Copy>
                            <Flex flexWrap='wrap'>
                                <Box w={['100%', '100%', '50%']} mt='1em' pr={['0', '0', '0.5em']} >
                                    <AddressField
                                      dataTest='claim-address-field'
                                      placeholder={intl.formatMessage(messages.addressPlaceholder)}
                                      label={intl.formatMessage(messages.address)}
                                      width='100%'
                                    />
                                </Box><Box pl={['0', '0', '0.5em']} mt='1em' w={['100%', '100%', '50%']}>
                                    <PostalCodeField
                                      dataTest='claim-postalCode-field'
                                      width='100%'
                                      label={intl.formatMessage(messages.postalCode)}
                                      placeholder={intl.formatMessage(messages.postalCodePlaceholder)}
                                    />
                                </Box>
                            </Flex>
                            <Flex flexWrap='wrap'>
                                <Box w={['100%', '100%', '50%']} mt='1em' pr={['0', '0', '0.5em']}>
                                    <TownField
                                      dataTest='claim-town-field'
                                      width='100%'
                                      validateIcon
                                      placeholder={intl.formatMessage(messages.townPlaceholder)}
                                      label={intl.formatMessage(messages.town)}
                                      model='.town'
                                    />
                                </Box><Box pl={['0', '0', '0.5em']} mt='1em' w={['100%', '100%', '50%']}>
                                    <ProvinceField
                                      dataTest='claim-province-field'
                                      width='100%'
                                      value={province}
                                      onChange={p => setProvince(p)}
                                      provinces={provinces || []}
                                      renderPlaceholder={() =>
                                        loadingProvinces
                                        ? <Box mt='1em'><DotsLoader /></Box>
                                        : intl.formatMessage(messages.province)
                                      }
                                    />
                                </Box>
                            </Flex>
                        </Box>
                        <Box w='100%' mt='1em'>
                            <FilesField
                              width='100%'
                              maxMegaBytesSize={MAX_MBYTES_FILESIZE}
                              leftIcon={<Icon type='doc' color='gray' />}
                              label={intl.formatMessage(messages.files)}
                            />
                            <Copy textAlign='right' fontSize='xSmall' fontColor='lightGray' fontWeight='regular'>
                                <Icon type='warning' color='lightGray' /> <FormattedMessage
                                  defaultMessage='Tamaño máximo: {size}Mb'
                                  values={{
                                      size: MAX_MBYTES_FILESIZE,
                                  }}
                                />
                            </Copy>
                        </Box>
                        <Box w='100%' mt='1em' textAlign='right'>
                            <LoaderButton
                              type='submit'
                              dataTest='claim-send'
                              formNoValidate
                              >
                                <FormattedMessage defaultMessage='Realizar reclamación' /> <Icon type='right' color='white' />
                            </LoaderButton>
                        </Box>
                    </Box>
                </Flex>
            </LocalForm>
        </Container>
    )
}

Form.propTypes = {
    fine: PropTypes.shape({
        bulletinNumber: PropTypes.string,
        isCancelled: PropTypes.bool,
    }).isRequired,
    onBack: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    getProvinces: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
}

export default injectIntl(Form)
